import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Smartphone, Monitor, Apple, Share, MoreVertical, Download,
  HelpCircle, ChevronDown, ChevronUp, AlertTriangle, Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Botao } from "@/components/sistema";

/**
 * Como instalar o app na tela de início.
 *
 * É a tela mais importante do app para este público: quem não instala fica
 * preso no navegador, perde o atalho e volta como ticket de suporte. Por isso
 * ela foi refeita na régua — e não é exagero de acessibilidade, é a razão da
 * tela existir.
 *
 * O que mudou, e por quê:
 *
 * - Cada passo tinha um emoji dentro da bolinha, um por passo. O emoji tentava
 *   desenhar o botão do navegador e não desenhava: três tracinhos não são o
 *   menu do Chrome, e um tique verde não é nada. No lugar entra o NÚMERO do
 *   passo, que é a única informação que a bolinha carregava de verdade — a
 *   ordem. O desenho do botão de verdade continua no quadro "o botão é assim",
 *   com ícone de traço, que é onde ele ajuda.
 *
 * - As dicas do "ainda não aparece?" eram uma lista de bullets com emoji,
 *   exatamente o que o dono proíbe. Viraram linhas de texto.
 *
 * - Tamanhos: o texto do passo era 14px e a dica 11px. Aqui é --t-corpo (18px)
 *   e --t-apoio (17px). Quem está instalando o app está de óculos, segurando o
 *   celular com uma mão e lendo o passo na outra tela.
 *
 * - Cor: o aviso de "já está instalado" era verde e o botão de compartilhar
 *   era azul. Verde no app só existe no semáforo glicêmico, e azul não é cor
 *   deste app. Os dois passaram a usar a tinta e o fio normais.
 *
 * Lógica intocada: toda a detecção de dispositivo/navegador, os exports
 * (isIosSafari, isAndroid, isStandalone), as abas, o caminho do iOS 26 vs.
 * clássico, o botão de instalação nativa e todas as chaves de i18n.
 */

type DeviceTab = "android" | "ios" | "desktop";

/* ── Device & browser detection ── */
// iPadOS 13+ Safari reports a desktop Mac UA by default, so a UA-only test
// misses every modern iPad. Declared first — detectDevice and isIosSafari
// both build on it.
const isIpad = () => /iPad/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const detectDevice = (): DeviceTab => {
  const ua = navigator.userAgent;
  if (/iPhone|iPod/.test(ua) || isIpad()) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
};

const isSamsungBrowser = () => /SamsungBrowser/i.test(navigator.userAgent);
export const isIosSafari = () => {
  const ua = navigator.userAgent;
  const isIosDevice = /iPhone|iPod/.test(ua) || isIpad();
  return isIosDevice && /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|OPiOS/.test(ua);
};
export const isAndroid = () => /Android/.test(navigator.userAgent);
const isIosChrome = () => /CriOS/.test(navigator.userAgent);
const isIosFirefox = () => /FxiOS/.test(navigator.userAgent);
const getIosMajorVersion = (): number | null => {
  const match = navigator.userAgent.match(/OS (\d+)_/);
  return match ? parseInt(match[1], 10) : null;
};
/** iOS 26 moved the Share button behind a "•••" menu next to the address bar. */
const isIos26Plus = () => {
  const v = getIosMajorVersion();
  return v !== null && v >= 26;
};
export const isStandalone = () =>
  (window.navigator as any).standalone === true ||
  window.matchMedia("(display-mode: standalone)").matches;

interface InstallGuideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNativeInstall?: () => void;
  iosOnly?: boolean;
}

type Passo = { num: number; text: string; hint?: string | null };

/** Um passo: número na bolinha, frase ao lado, e a dica de onde olhar. */
function LinhaPasso({ passo }: { passo: Passo }) {
  return (
    <div className="flex items-start gap-4">
      <span
        className="flex shrink-0 items-center justify-center rounded-full font-bold"
        style={{
          /* 40px é o menor círculo em que o número ainda se lê de relance sem
             passar por alvo de toque — o passo não é clicável. */
          width: 40,
          height: 40,
          background: "hsl(var(--secondary))",
          color: "var(--tinta)",
          fontSize: "var(--t-linha)",
        }}
      >
        {passo.num}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className="font-semibold leading-snug"
          style={{ fontSize: "var(--t-corpo)", color: "var(--tinta)", paddingTop: 6 }}
        >
          {passo.text}
        </p>
        {passo.hint && (
          <p
            className="leading-snug"
            style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)", paddingTop: 4 }}
          >
            {passo.hint}
          </p>
        )}
      </div>
    </div>
  );
}

const InstallGuideModal = ({ open, onOpenChange, onNativeInstall, iosOnly }: InstallGuideModalProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<DeviceTab>(iosOnly ? "ios" : detectDevice());
  const [showHelp, setShowHelp] = useState(false);
  const [alreadyInstalled, setAlreadyInstalled] = useState(false);

  useEffect(() => {
    if (!open) return;
    const device = iosOnly ? "ios" : detectDevice();
    setActiveTab(device);
    setShowHelp(false);
    setAlreadyInstalled(isStandalone());
  }, [open, iosOnly]);

  const handleTabChange = useCallback((tab: DeviceTab) => {
    setActiveTab(tab);
    setShowHelp(false);
  }, []);

  const handleHelpToggle = () => {
    setShowHelp((v) => !v);
  };

  const handleClose = (v: boolean) => {
    onOpenChange(v);
  };

  const tabs: { key: DeviceTab; label: string; icon: React.ReactNode }[] = [
    { key: "android", label: "Android", icon: <Smartphone className="h-5 w-5" strokeWidth={2} /> },
    { key: "ios",     label: "iPhone",  icon: <Apple className="h-5 w-5" strokeWidth={2} /> },
    { key: "desktop", label: t("install_guide.computer"), icon: <Monitor className="h-5 w-5" strokeWidth={2} /> },
  ];
  const visibleTabs = iosOnly ? tabs.filter((tab) => tab.key === "ios") : tabs;

  /* ── iOS: wrong browser warning ── */
  const showIosWrongBrowser = activeTab === "ios" && (isIosChrome() || isIosFirefox());
  const wrongBrowserName = isIosChrome() ? "Chrome" : isIosFirefox() ? "Firefox" : "este navegador";

  /* ── Steps per device ── */
  const androidSteps: Passo[] = isSamsungBrowser()
    ? [
        { num: 1, text: t("install_guide.android_samsung_1") },
        { num: 2, text: t("install_guide.android_samsung_2") },
        { num: 3, text: t("install_guide.android_samsung_3") },
      ]
    : [
        { num: 1, text: t("install_guide.android_chrome_1") },
        { num: 2, text: t("install_guide.android_chrome_2") },
        { num: 3, text: t("install_guide.android_chrome_3") },
      ];

  const iosDevice = isIpad() ? t("install_guide.ipad") : "iPhone";
  const isIos26 = isIos26Plus();

  // iOS 26 moved Share behind a "•••" menu next to the address bar (See More → Add to Home Screen).
  const iosStepsModern: Passo[] = [
    { num: 1, text: t("install_guide.ios26_step1"), hint: t("install_guide.ios26_step1_hint") },
    { num: 2, text: t("install_guide.ios26_step2"), hint: null },
    { num: 3, text: t("install_guide.ios26_step3"), hint: null },
    { num: 4, text: t("install_guide.ios26_step4"), hint: null },
    { num: 5, text: t("install_guide.ios_step4", { device: iosDevice }), hint: null },
  ];

  // Classic flow: Share button at the bottom of Safari → Add to Home Screen.
  const iosStepsClassic: Passo[] = [
    { num: 1, text: t("install_guide.ios_step1", { device: iosDevice }), hint: t("install_guide.ios_step1_hint") },
    { num: 2, text: t("install_guide.ios_step2"), hint: t("install_guide.ios_step2_hint") },
    { num: 3, text: t("install_guide.ios_step3"), hint: null },
    { num: 4, text: t("install_guide.ios_step4", { device: iosDevice }), hint: null },
  ];

  const iosSteps = isIos26 ? iosStepsModern : iosStepsClassic;

  const desktopSteps: Passo[] = [
    { num: 1, text: t("install_guide.desktop_1") },
    { num: 2, text: t("install_guide.desktop_2") },
    { num: 3, text: t("install_guide.desktop_3") },
    { num: 4, text: t("install_guide.desktop_4") },
  ];

  /* ── Dicas de "ainda não aparece?" — texto, não lista de emoji ── */
  const iosHelpTips = [
    t("install_guide.ios_help_1"),
    t("install_guide.ios_help_2"),
    t("install_guide.ios_help_3"),
    t("install_guide.ios_help_4"),
  ];
  const androidHelpTips = [
    t("install_guide.android_help_1"),
    t("install_guide.android_help_2"),
    t("install_guide.android_help_3"),
  ];
  const helpTips = activeTab === "ios" ? iosHelpTips : androidHelpTips;

  const passos = activeTab === "ios" ? iosSteps : activeTab === "android" ? androidSteps : desktopSteps;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm overflow-hidden rounded-2xl border-border bg-card p-0">

        {/* Cabeçalho */}
        <DialogHeader style={{ padding: "20px 20px 12px" }}>
          <DialogTitle
            className="text-center font-bold leading-tight"
            style={{ fontSize: "var(--t-item)", color: "var(--tinta)" }}
          >
            {t("install_guide.title")}
          </DialogTitle>
          <p
            className="text-center leading-snug"
            style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)", paddingTop: 4 }}
          >
            {t("install_guide.subtitle_short")}
          </p>
        </DialogHeader>

        {/* Já está instalado */}
        {alreadyInstalled && (
          <div
            className="mx-5 mb-3 flex items-center gap-3 rounded-xl border border-border px-4"
            style={{ minHeight: "var(--alvo-min)" }}
          >
            <Check className="h-6 w-6 shrink-0" strokeWidth={2.5} style={{ color: "var(--tinta)" }} />
            <p className="font-semibold leading-snug" style={{ fontSize: "var(--t-apoio)" }}>
              {t("install_guide.already_installed")}
            </p>
          </div>
        )}

        {/* Abas de aparelho — alvo de 56px, que é o piso do app */}
        {visibleTabs.length > 1 && (
          <div className="flex gap-2" style={{ padding: "0 20px 16px" }}>
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                aria-pressed={activeTab === tab.key}
                onClick={() => handleTabChange(tab.key)}
                className="flex flex-1 flex-col items-center justify-center gap-1 rounded-xl font-bold"
                style={{
                  minHeight: "var(--alvo-min)",
                  fontSize: "var(--t-apoio)",
                  ...(activeTab === tab.key
                    ? { background: "hsl(var(--accent))", color: "hsl(var(--accent-foreground))" }
                    : { background: "transparent", color: "var(--tinta-apoio)", border: "2px solid hsl(var(--border))" }),
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div style={{ padding: "0 20px 20px" }} className="space-y-5">

          {/* iOS aberto no navegador errado */}
          {showIosWrongBrowser && (
            <div className="flex items-start gap-3 rounded-xl border border-border p-4">
              <AlertTriangle
                className="mt-0.5 h-6 w-6 shrink-0"
                strokeWidth={2}
                style={{ color: "var(--tinta)" }}
              />
              <div className="min-w-0">
                <p className="font-bold leading-snug" style={{ fontSize: "var(--t-corpo)" }}>
                  {t("install_guide.ios_wrong_browser_title", { browser: wrongBrowserName })}
                </p>
                <p
                  className="leading-snug"
                  style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)", paddingTop: 4 }}
                >
                  {t("install_guide.ios_wrong_browser_desc")}
                </p>
              </div>
            </div>
          )}

          {/* Passos — um formato só para os três aparelhos */}
          {!(activeTab === "ios" && showIosWrongBrowser) && (
            <div className="space-y-4">
              {passos.map((passo) => (
                <LinhaPasso key={passo.num} passo={passo} />
              ))}
            </div>
          )}

          {/* "O botão é assim" — aqui o ícone de traço ajuda, porque desenha o
              controle que a pessoa tem de achar no navegador. */}
          {activeTab === "ios" && !showIosWrongBrowser && (
            <div className="flex items-center gap-3 rounded-xl border border-border p-4">
              <span
                className="flex shrink-0 items-center justify-center rounded-lg"
                style={{ width: 44, height: 44, background: "hsl(var(--secondary))" }}
              >
                {isIos26 ? (
                  <MoreVertical className="h-6 w-6 rotate-90" strokeWidth={2.2} style={{ color: "var(--tinta)" }} />
                ) : (
                  <Share className="h-6 w-6" strokeWidth={2.2} style={{ color: "var(--tinta)" }} />
                )}
              </span>
              <p
                className="min-w-0 flex-1 leading-snug"
                style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)" }}
              >
                {isIos26
                  ? t("install_guide.ios26_dots_looks_like")
                  : t("install_guide.ios_share_looks_like")}
              </p>
            </div>
          )}

          {/* Instalação nativa, quando o navegador oferece */}
          {onNativeInstall && (
            <Botao
              rotulo={t("install_guide.install_now")}
              icone={Download}
              onClick={() => { onNativeInstall(); handleClose(false); }}
            />
          )}

          {/* "Ainda não aparece?" */}
          {(activeTab === "ios" || activeTab === "android") && (
            <button
              type="button"
              onClick={handleHelpToggle}
              aria-expanded={showHelp}
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-border font-bold"
              style={{ minHeight: "var(--alvo-min)", fontSize: "var(--t-apoio)", color: "var(--tinta)" }}
            >
              <HelpCircle className="h-5 w-5 shrink-0" strokeWidth={2.2} />
              {t("install_guide.still_not_seeing")}
              {showHelp
                ? <ChevronUp className="h-5 w-5 shrink-0" strokeWidth={2.5} />
                : <ChevronDown className="h-5 w-5 shrink-0" strokeWidth={2.5} />}
            </button>
          )}

          {/* Dicas */}
          {showHelp && (
            <div className="rounded-xl border border-border p-4">
              <p
                className="font-bold uppercase"
                style={{ fontSize: "var(--t-etiqueta)", letterSpacing: "0.14em", color: "var(--tinta-apoio)" }}
              >
                {t("install_guide.help_check_these")}
              </p>
              <div className="pt-3">
                {helpTips.map((tip, i) => (
                  <p
                    key={i}
                    className="leading-snug"
                    style={{
                      fontSize: "var(--t-apoio)",
                      color: "var(--tinta-apoio)",
                      paddingTop: i === 0 ? 0 : 12,
                    }}
                  >
                    {tip}
                  </p>
                ))}
              </div>
              <p
                className="leading-snug"
                style={{
                  fontSize: "var(--t-apoio)",
                  color: "var(--tinta-apoio)",
                  marginTop: 16,
                  paddingTop: 16,
                  borderTop: "1px solid hsl(var(--border))",
                }}
              >
                {t("install_guide.help_still_stuck")}
                {" "}
                <a
                  href="/support"
                  className="font-bold underline underline-offset-2"
                  style={{ color: "var(--tinta)" }}
                  onClick={() => handleClose(false)}
                >
                  {t("install_guide.help_contact")}
                </a>
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InstallGuideModal;
